import { updateQueryStatus, fetchQueryDetail, getInvoiceDetailsByAccountCode, assignQuery, updateQueryDetails } from "@api";
import { getServerErrorMessage } from "@api";
import {
  Badge,
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  GridSection,
  GridSectionBody,
  GridSectionHeader,
  GridSectionItem,
  GridSectionSpan,
  InvoiceDetailsModal,
  Timeline,
} from "@components";
import { Flag, CheckLg, Eye, ReceiptCutoff, PersonCheck, PencilSquare, Paperclip } from "react-bootstrap-icons";
import { formatAmount, formatDateTime } from "@utils";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryReasonLabels as fallbackReasonLabels, queryStatusColors, queryStatusLabels, queryDaysUntilDue, isQueryOverdue, isTerminalQueryStatus } from "../common";
import { useMerchant, updateCustomer } from "@auth";
import { toast } from "@components/toast/Toast";
import AssignQueryModal from "../components/AssignQueryModal";
import ResolveQueryModal from "../components/ResolveQueryModal";


const queryInvoicesLabel = (invoices) => {
  if (!invoices?.length) return "—";
  if (invoices.length === 1) return invoices[0].invoiceNumber;
  return `${invoices.length} invoices`;
};

const formatFileSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const statusNode = (status) => {
  if (status === "Resolved") return { icon: CheckLg, color: "success" };
  if (status === "UnderReview") return { icon: Eye, color: "amber" };
  return { icon: Flag, color: "primary" };
};

const toTimelineActivity = (timeline, statusLabels) =>
  (timeline ?? []).map((entry) => {
    if (entry.type === "Created") {
      return {
        title: "Query raised",
        subTitle: `${formatDateTime(entry.createdAt)}${entry.actor ? ` - ${entry.actor}` : ""}`,
        date: entry.createdAt,
        nodeContent: { icon: Flag, color: "primary" },
      };
    }

    if (entry.type === "StatusChanged") {
      return {
        title: `${statusLabels[entry.fromStatus] ?? entry.fromStatus} → ${statusLabels[entry.toStatus] ?? entry.toStatus}`,
        subTitle: `${formatDateTime(entry.createdAt)}${entry.actor ? ` - ${entry.actor}` : ""}`,
        content: entry.note,
        date: entry.createdAt,
        nodeContent: statusNode(entry.toStatus),
      };
    }

    if (entry.type === "AssignmentChanged") {
      return {
        title: `Assigned to ${entry.note}`,
        subTitle: `${formatDateTime(entry.createdAt)}${entry.actor ? ` - ${entry.actor}` : ""}`,
        date: entry.createdAt,
        nodeContent: { icon: PersonCheck, color: "primary" },
      };
    }

    if (entry.type === "DescriptionChanged") {
      return {
        title: "Description updated",
        subTitle: `${formatDateTime(entry.createdAt)}${entry.actor ? ` - ${entry.actor}` : ""}`,
        content: entry.note,
        date: entry.createdAt,
        nodeContent: { icon: PencilSquare, color: "primary" },
      };
    }

    return {
      title: entry.note ?? entry.type,
      subTitle: `${formatDateTime(entry.createdAt)}${entry.actor ? ` - ${entry.actor}` : ""}`,
      date: entry.createdAt,
      nodeContent: {},
    };
  });

const QueryDetails = ({ query, reasonLabels = fallbackReasonLabels, onClose, onChange }) => {
  const [updating, setUpdating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsDraft, setDetailsDraft] = useState("");
  const [savingDetails, setSavingDetails] = useState(false);
  const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useState(null);
  const queryClient = useQueryClient();
  const { hasPermission, merchant } = useMerchant();
  const canUpdate = hasPermission(updateCustomer);

  const { data: detail } = useQuery({
    queryKey: ["queries", "detail", query?.id],
    queryFn: ({ signal }) => fetchQueryDetail(query.id, { signal }),
    enabled: !!query?.id,
    staleTime: 30 * 1000,
  });

  // Prefer the freshly-fetched detail so assigning/reassigning is reflected without closing the drawer.
  const status = detail?.status ?? query?.status;
  const assignedToName = detail?.assignedToName ?? query?.assignedToName;
  const assignedToUserId = detail?.assignedToUserId ?? query?.assignedToUserId;
  const details = detail?.details ?? query?.details;
  const priority = detail?.priority ?? query?.priority;
  const resolutionReason = detail?.resolutionReason;
  const resolutionComment = detail?.resolutionComment;
  const attachments = detail?.attachments ?? [];

  const invoiceNumbers = useMemo(
    () => (query?.invoices ?? []).map((i) => i.invoiceNumber).filter(Boolean),
    [query],
  );

  const { data: invoiceDetailsArray } = useQuery({
    queryKey: ["queries", "invoice-details", query?.accountCode, invoiceNumbers],
    queryFn: () => getInvoiceDetailsByAccountCode(query.accountCode, invoiceNumbers),
    enabled: !!query?.accountCode && invoiceNumbers.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const invoiceDetailsMap = useMemo(() => {
    if (!invoiceDetailsArray) return {};
    return Object.fromEntries(invoiceDetailsArray.map((d) => [d.number, d]));
  }, [invoiceDetailsArray]);

  const handleResolve = async (resolution) => {
    setUpdating(true);
    try {
      await updateQueryStatus(query.id, "Resolved", resolution);
      queryClient.invalidateQueries({ queryKey: ["queries"] });
      onChange?.();
      setResolveOpen(false);
      onClose();
    } catch (error) {
      toast({
        title: "Couldn't resolve the query",
        description: getServerErrorMessage(error) || "Please try again.",
        timeoutSeconds: 5,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleAssign = async (assignee) => {
    setAssigning(true);
    try {
      await assignQuery(query.id, { assignedToUserId: assignee.id, assignedToName: assignee.name });
      queryClient.invalidateQueries({ queryKey: ["queries"] });
      queryClient.invalidateQueries({ queryKey: ["queries", "detail", query.id] });
      onChange?.();
      setAssignOpen(false);
    } catch (error) {
      toast({
        title: "Couldn't assign the query",
        description: getServerErrorMessage(error) || "Please try again.",
        timeoutSeconds: 5,
      });
    } finally {
      setAssigning(false);
    }
  };

  const startEditDetails = () => {
    setDetailsDraft(details ?? "");
    setEditingDetails(true);
  };

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    try {
      await updateQueryDetails(query.id, detailsDraft);
      queryClient.invalidateQueries({ queryKey: ["queries"] });
      queryClient.invalidateQueries({ queryKey: ["queries", "detail", query.id] });
      onChange?.();
      setEditingDetails(false);
    } catch (error) {
      toast({
        title: "Couldn't update the description",
        description: getServerErrorMessage(error) || "Please try again.",
        timeoutSeconds: 5,
      });
    } finally {
      setSavingDetails(false);
    }
  };


  return (
    <Drawer
      isOpen={!!query}
      toggle={onClose}
      title={
        query ? (
          <span className="flex items-center gap-2">
            <span>Query — {queryInvoicesLabel(query.invoices)}</span>
            <Badge color={queryStatusColors[status]}>
              {queryStatusLabels[status] ?? status}
            </Badge>
          </span>
        ) : "Query"
      }
    >
      {query && (
        <>
          <DrawerBody>
          <GridSection>
            <GridSectionItem>
              <GridSectionHeader>Submitted</GridSectionHeader>
              <GridSectionBody>{formatDateTime(query.createdAt)}</GridSectionBody>
            </GridSectionItem>
            {query.reason && (
              <GridSectionItem>
                <GridSectionHeader>Reason</GridSectionHeader>
                <GridSectionBody>
                  {reasonLabels[query.reason] ?? query.reason}
                </GridSectionBody>
              </GridSectionItem>
            )}
            <GridSectionItem>
              <GridSectionHeader>Account</GridSectionHeader>
              <GridSectionBody>{query.accountCode ?? "—"}</GridSectionBody>
            </GridSectionItem>
            {priority != null && (
              <GridSectionItem>
                <GridSectionHeader>Priority</GridSectionHeader>
                <GridSectionBody>{priority} / 10</GridSectionBody>
              </GridSectionItem>
            )}
            <GridSectionItem>
              <GridSectionHeader>Total amount</GridSectionHeader>
              <GridSectionBody>{formatAmount(query.amount)}</GridSectionBody>
            </GridSectionItem>
            <GridSectionItem>
              <GridSectionHeader>Due</GridSectionHeader>
              <GridSectionBody>
                {isTerminalQueryStatus(status) ? (
                  "Complete"
                ) : isQueryOverdue({ ...query, status }) ? (
                  <Badge color="red">Overdue</Badge>
                ) : (
                  `${queryDaysUntilDue(query.dueBy)} days`
                )}
              </GridSectionBody>
            </GridSectionItem>
            <GridSectionItem>
              <GridSectionHeader>Assigned to</GridSectionHeader>
              <GridSectionBody>
                <span className="inline-flex items-center gap-1.5">
                  {assignedToName ?? <span className="text-zinc-400">Unassigned</span>}
                  {canUpdate && !isTerminalQueryStatus(status) && (
                    <button
                      type="button"
                      aria-label={assignedToName ? "Reassign query" : "Assign query"}
                      onClick={() => setAssignOpen(true)}
                      className="text-zinc-400 hover:text-zinc-600"
                    >
                      <PencilSquare className="size-3.5" />
                    </button>
                  )}
                </span>
              </GridSectionBody>
            </GridSectionItem>
            <GridSectionSpan>
              <div className="flex flex-col gap-1 w-full">
                <GridSectionHeader>{query.invoices?.length === 1 ? "Invoice" : "Invoices"}</GridSectionHeader>
                <div className="flex flex-col gap-2 mt-1">
                  {(query.invoices ?? []).map((invoice) => {
                    const invoiceDetails = invoiceDetailsMap[invoice.invoiceNumber];
                    return (
                      <div
                        key={invoice.ledgerTransactionId}
                        className="flex items-center justify-between rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2.5 text-sm"
                      >
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-zinc-800">{invoice.invoiceNumber}</span>
                          {invoiceDetails && (
                            <button
                              onClick={() => setSelectedInvoiceDetails(invoiceDetails)}
                              className="text-primary-600 text-xs flex items-center gap-1"
                            >
                              Invoice details
                              <ReceiptCutoff className="size-3 mx-0.5" />
                            </button>
                          )}
                        </div>
                        <span className="text-zinc-600 tabular-nums">{formatAmount(invoice.amount)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </GridSectionSpan>
            {query.lineItems?.length > 0 && (
              <GridSectionSpan>
                <div className="flex flex-col gap-1 w-full">
                  <GridSectionHeader>{query.lineItems.length === 1 ? "Line item" : "Line items"}</GridSectionHeader>
                  <div className="flex flex-col gap-2 mt-1">
                    {query.lineItems.map((li) => (
                      <div key={li.lineItemId} className="rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2.5 text-sm">
                        <p className="font-medium text-zinc-800">{li.description ?? "—"}</p>
                        <p className="text-zinc-500 text-xs mt-0.5">
                          {reasonLabels[li.reason] ?? li.reason}
                        </p>
                        {li.details && (
                          <p className="text-zinc-600 text-xs mt-1 whitespace-pre-wrap">{li.details}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </GridSectionSpan>
            )}
            {(details || !query.lineItems?.length) && (
              <GridSectionSpan>
                <div className="flex flex-col gap-1 w-full">
                  <div className="flex items-center gap-1.5">
                    <GridSectionHeader>Details</GridSectionHeader>
                    {canUpdate && !isTerminalQueryStatus(status) && !editingDetails && (
                      <button
                        type="button"
                        aria-label="Edit description"
                        onClick={startEditDetails}
                        className="text-zinc-400 hover:text-zinc-600"
                      >
                        <PencilSquare className="size-3.5" />
                      </button>
                    )}
                  </div>
                  {editingDetails ? (
                    <div className="flex flex-col gap-2 mt-1">
                      <textarea
                        value={detailsDraft}
                        onChange={(e) => setDetailsDraft(e.target.value)}
                        rows={4}
                        maxLength={500}
                        placeholder="Describe the issue with this invoice..."
                        className="block w-full rounded-xl border border-zinc-200 px-3 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                      />
                      <div className="flex gap-2 justify-end">
                        <Button color="secondary" className="h-8" onClick={() => setEditingDetails(false)} disabled={savingDetails}>
                          Cancel
                        </Button>
                        <Button color="primary" className="h-8" onClick={handleSaveDetails} disabled={savingDetails} submitting={savingDetails}>
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <GridSectionBody>
                      {details ? (
                        <p className="whitespace-pre-wrap text-sm text-zinc-700">{details}</p>
                      ) : (
                        <p className="text-sm text-zinc-400 italic">No details provided</p>
                      )}
                    </GridSectionBody>
                  )}
                </div>
              </GridSectionSpan>
            )}
            {attachments.length > 0 && (
              <GridSectionSpan>
                <div className="flex flex-col gap-1 w-full">
                  <GridSectionHeader>Attachments</GridSectionHeader>
                  <div className="flex flex-col gap-2 mt-1">
                    {attachments.map((a) => (
                      <a
                        key={a.id}
                        href={a.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-between gap-2 rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2.5 text-sm hover:border-zinc-200 hover:bg-zinc-100"
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <Paperclip className="size-3.5 shrink-0 text-zinc-400" />
                          <span className="truncate text-primary-600">{a.fileName}</span>
                        </span>
                        <span className="shrink-0 text-xs text-zinc-400 tabular-nums">{formatFileSize(a.size)}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </GridSectionSpan>
            )}
            {resolutionReason && (
              <GridSectionSpan>
                <div className="flex flex-col gap-1 w-full">
                  <GridSectionHeader>Resolution</GridSectionHeader>
                  <GridSectionBody>
                    <p className="text-sm text-zinc-700">{resolutionReason}</p>
                    {resolutionComment && (
                      <p className="text-zinc-600 text-xs mt-1 whitespace-pre-wrap">{resolutionComment}</p>
                    )}
                  </GridSectionBody>
                </div>
              </GridSectionSpan>
            )}
          </GridSection>

          {(query.customerName || query.customerEmail || query.customerPhone) && (
            <GridSection>
              {query.customerName && (
                <GridSectionItem>
                  <GridSectionHeader>Customer</GridSectionHeader>
                  <GridSectionBody>{query.customerName}</GridSectionBody>
                </GridSectionItem>
              )}
              {query.customerEmail && (
                <GridSectionItem>
                  <GridSectionHeader>Email</GridSectionHeader>
                  <GridSectionBody>
                    <a href={`mailto:${query.customerEmail}`} className="text-primary-600 hover:underline">
                      {query.customerEmail}
                    </a>
                  </GridSectionBody>
                </GridSectionItem>
              )}
              {query.customerPhone && (
                <GridSectionItem>
                  <GridSectionHeader>Phone</GridSectionHeader>
                  <GridSectionBody>
                    <a href={`tel:${query.customerPhone}`} className="text-primary-600 hover:underline">
                      {query.customerPhone}
                    </a>
                  </GridSectionBody>
                </GridSectionItem>
              )}
            </GridSection>
          )}

          {detail?.timeline?.length > 0 && (
            <Timeline activity={toTimelineActivity(detail.timeline, queryStatusLabels)} />
          )}
          </DrawerBody>

          {canUpdate && status === "UnderReview" && (
            <DrawerFooter>
              <Button
                color="primary"
                className="ml-auto"
                onClick={() => setResolveOpen(true)}
                disabled={updating}
              >
                Mark as resolved
              </Button>
            </DrawerFooter>
          )}

          <InvoiceDetailsModal
            invoice={selectedInvoiceDetails}
            isOpen={!!selectedInvoiceDetails}
            onClose={() => setSelectedInvoiceDetails(null)}
            orgId={merchant?.organisationId}
          />

          <AssignQueryModal
            isOpen={assignOpen}
            onClose={() => setAssignOpen(false)}
            onAssign={handleAssign}
            submitting={assigning}
            currentAssigneeId={assignedToUserId}
            currentAssigneeName={assignedToName}
          />

          <ResolveQueryModal
            isOpen={resolveOpen}
            onClose={() => setResolveOpen(false)}
            onResolve={handleResolve}
            submitting={updating}
          />
        </>
      )}
    </Drawer>
  );
};

export default QueryDetails;
